import React, { Children, cloneElement, useState, useEffect, forwardRef, useImperativeHandle } from "react";

export default forwardRef(function Table({children, data, columns, filters, statusFilters}, ref) {    
    const [tableData, setTableData] = useState(defaultData());
    const [sortOrder, setSortOrder] = useState(""); // "desc" or "asc"
    const [configTableData, setConfigTableData] = useState(initTableData());
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    
    // Init Table Data based on the selected columns
    // Use the key to corresponds the column key and the object data key
    function initTableData() {
        // Loop on each table data row
        let tableData = data.map((d) => {
            // Always set the id to used on callback functions
            let cell = { id: d.id };
            // Find the value corresponding to each column name
            columns.forEach((column) => {
                // Loop through the object to get the values
                const path = column.key.split(".");
                
                let results = [];
                let dataLoop = d;
                
                path.forEach((item, index) => {
                    if (index + 1 < path.length) {
                        // Loop through keys in the object
                        dataLoop = dataLoop[item];
                    } else {
                        if (Array.isArray(dataLoop)) {
                            // Get Value but it's an array
                            dataLoop.forEach((object) => {
                                results.push(object[item]);
                            })
                        } else {
                            // Push the only value
                            results = dataLoop[item];
                        }  
                    }
                })                
                cell[column.key] = results; //d[c.key]; // Add key name and relative value to the table data
            })
            
            return cell;
        });
        return tableData;
    }
    
    /**
     * Initiate the default table data with filtering archived and deleted status
     * @returns 
     */
    function defaultData() {
        let dataTMP = initTableData();
        return defaultFilterArchivedAndDeleted(dataTMP);
    }

    /**
     * On default filter - do not display archived and deleted specific statuses
     * @param {*} tableDataV 
     * @returns 
     */
    function defaultFilterArchivedAndDeleted(tableDataV) {
        let result = [...tableDataV];

        return (
            result.filter((data) => {   
                // Look for the value if it's an array of data in the cell
                if (Array.isArray(data["status"])) {
                    return (
                        data["status"].findIndex((v) => {                            
                            return (v.toString() != "archived" && v.toString() != "deleted")
                        }) > -1
                    )
                    }
                else                
                    return (data["status"]?.toString() != "archived" && data["status"]?.toString() != "deleted")                    
            })
        )
    }

    // Update table data when props has changed
    useEffect(() => {
        setTableData(defaultData());
        setConfigTableData(initTableData());
    }, [data]);

    // Add several category Ids or remove it from the selectedRowids table
    function handleRowSelection(rowId, isChecked) {
        if (isChecked && !selectedRowIds.includes(rowId))
            setSelectedRowIds([...selectedRowIds, rowId]);
        else
            setSelectedRowIds(selectedRowIds.filter(id => id !== rowId));
    };

    // Add or remove all the categories ID in th selectedRowids table
    function handleAllCheckedSelection(isChecked) {        
        if (isChecked) {
            setSelectedRowIds(tableData.map(c => c.id));
        } else {
            setSelectedRowIds([]);
        }
    }

    useImperativeHandle(ref, () => ({
        getSelectedRows: () => selectedRowIds,
        clearSelection: () => setSelectedRowIds([]),
    }))

    /**
     * Sort the table data based on key
     * @param {} key 
     */
    function sort(key) {        
        let sorting = sortOrder == "asc" ? "desc" : "asc";
        let table = [];

        if (sorting == "asc") {
            table = tableData.sort((a, b) => a[key] < b[key] ? -1 : 1);
        } else {
            table = tableData.sort((a, b) => a[key] < b[key] ? 1 : -1);
        }

        setSortOrder(sorting);
        setTableData(table);
    }
    
    /**
     * Filter the table content
     * @param {*} event 
     * @param {*} filter 
     */
    function filter(option, filter) {
        let selectedValue = option.value;
        let result = [...configTableData];

        if (selectedValue != "all") {        
            result = result.filter((data) => {   
                // Look for the value if it's an array of data in the cell
                if (Array.isArray(data[filter.key])) {
                    return (
                        data[filter.key].findIndex((v) => {                            
                            return (v.toString() === selectedValue.toString())
                        }) > -1
                    )
                    }
                else                 
                    return (data[filter.key].toString() === selectedValue.toString());
            });
        } else {
            // Set default status
            result = defaultData();
        }

        setTableData(result);
    }

    return (
        Children.map(children, (child) => {
            return (cloneElement(child, { 
                tableData,
                columns,
                sort,
                filters,
                filterCallback: filter,
                statusFilters,
                selectedRowIds,
                handleRowSelection,
                handleAllCheckedSelection
            }));                
        })
    )
});